import { useState, useCallback } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Alert, Table, Badge, Pagination } from 'react-bootstrap';
import { FaDownload, FaUpload, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { AppSelect, AppAsyncSelect } from '../../common/_components/AppSelect';
import importacaoArquivosService from '../../api/suporte/importacaoArquivosService';
import api from '../../services/api';

const AREAS = [
    { value: 'pacientes', label: 'Pacientes' },
    { value: 'prestadores', label: 'Prestadores' },
    { value: 'agendamentos', label: 'Agendamentos' },
];

const CATEGORIAS_PRESTADORES = [
    'medicos', 'multiprofissionais', 'farmacias', 'hospitais', 'clinicas',
    'plataformas', 'servicos_diagnostico', 'laboratorio_diagnostico',
    'motoristas', 'outros', 'cooperativas_saude',
];

const STATUS_BADGE = {
    validado_ok: 'success',
    validado_com_erro: 'danger',
    importado_ok: 'success',
    importado_parcial: 'warning',
    importando: 'info',
    validando: 'info',
    falha: 'danger',
    cancelado: 'secondary',
};

const STATUS_LABEL = {
    validando: 'Validando',
    validado_ok: 'Validado com sucesso',
    validado_com_erro: 'Validado com erro',
    importando: 'Importando',
    importado_ok: 'Importado com sucesso',
    importado_parcial: 'Importado parcialmente',
    falha: 'Falha na importação',
    cancelado: 'Cancelado',
};

const PAGE_SIZE = 50;

/** Renderiza a tabela de erros + paginação */
function TabelaErros({ jobId, errosIniciais, totalErros }) {
    const [page, setPage] = useState(1);
    const [erros, setErros] = useState(errosIniciais || []);
    const [loadingPage, setLoadingPage] = useState(false);
    const [totalCount, setTotalCount] = useState(totalErros ?? errosIniciais?.length ?? 0);
    const [numPages, setNumPages] = useState(
        Math.max(1, Math.ceil((totalErros ?? errosIniciais?.length ?? 0) / PAGE_SIZE))
    );

    // Se há mais erros do que foram enviados inicialmente, busca via endpoint paginado
    const precisaPaginar = totalErros > PAGE_SIZE || (errosIniciais?.length ?? 0) >= PAGE_SIZE;

    const carregarPagina = useCallback(async (novaPagina) => {
        if (!precisaPaginar) return;
        setLoadingPage(true);
        try {
            const res = await importacaoArquivosService.errosPaginados(jobId, novaPagina, PAGE_SIZE);
            setErros(res.data.results);
            setTotalCount(res.data.count);
            setNumPages(res.data.num_pages);
            setPage(novaPagina);
        } catch {
            // fallback silencioso
        } finally {
            setLoadingPage(false);
        }
    }, [jobId, precisaPaginar]);

    if (!erros || erros.length === 0) return null;

    return (
        <div>
            <div style={{ maxHeight: 300, overflowY: 'auto', position: 'relative' }}>
                {loadingPage && (
                    <div className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{ background: 'rgba(255,255,255,0.7)', zIndex: 10, top: 0, left: 0 }}>
                        <Spinner animation="border" size="sm" />
                    </div>
                )}
                <Table size="sm" bordered hover className="mb-0">
                    <thead className="table-light sticky-top">
                        <tr>
                            <th style={{ width: 60 }}>Linha</th>
                            <th style={{ width: 120 }}>Coluna</th>
                            <th style={{ width: 80 }}>Sev.</th>
                            <th>Mensagem</th>
                        </tr>
                    </thead>
                    <tbody>
                        {erros.map((err, i) => (
                            <tr key={i} className={err.severidade === 'erro' ? 'table-danger' : 'table-warning'}>
                                <td>{err.linha ?? '-'}</td>
                                <td>{err.coluna || '-'}</td>
                                <td>
                                    <Badge bg={err.severidade === 'erro' ? 'danger' : 'warning'} text={err.severidade === 'erro' ? undefined : 'dark'}>
                                        {err.severidade}
                                    </Badge>
                                </td>
                                <td style={{ wordBreak: 'break-word' }}>{err.mensagem}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            {/* Paginação — só renderiza se precisar */}
            {precisaPaginar && numPages > 1 && (
                <div className="d-flex align-items-center justify-content-between mt-2">
                    <small className="text-muted">
                        {totalCount} ocorrência{totalCount !== 1 ? 's' : ''} · página {page} de {numPages}
                    </small>
                    <Pagination size="sm" className="mb-0">
                        <Pagination.First disabled={page === 1} onClick={() => carregarPagina(1)} />
                        <Pagination.Prev disabled={page === 1} onClick={() => carregarPagina(page - 1)} />
                        {Array.from({ length: Math.min(5, numPages) }, (_, i) => {
                            // Janela de 5 páginas centrada na atual
                            let start = Math.max(1, Math.min(page - 2, numPages - 4));
                            const p = start + i;
                            return p <= numPages ? (
                                <Pagination.Item key={p} active={p === page} onClick={() => p !== page && carregarPagina(p)}>
                                    {p}
                                </Pagination.Item>
                            ) : null;
                        })}
                        <Pagination.Next disabled={page === numPages} onClick={() => carregarPagina(page + 1)} />
                        <Pagination.Last disabled={page === numPages} onClick={() => carregarPagina(numPages)} />
                    </Pagination>
                </div>
            )}
        </div>
    );
}

export default function ImportacaoArquivosModal({ show, onHide, onJobConcluido }) {
    const [step, setStep] = useState(1);
    const [programaSelecionado, setProgramaSelecionado] = useState(null);
    const [areaSelecionada, setAreaSelecionada] = useState(null);
    const [arquivo, setArquivo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [jobResult, setJobResult] = useState(null);
    const [erroGlobal, setErroGlobal] = useState('');
    const [baixandoTemplate, setBaixandoTemplate] = useState(false);
    const [baixandoDoc, setBaixandoDoc] = useState(false);
    const [baixandoCsv, setBaixandoCsv] = useState(false);

    const handleClose = () => {
        const hadResult = !!jobResult;
        setStep(1);
        setProgramaSelecionado(null);
        setAreaSelecionada(null);
        setArquivo(null);
        setJobResult(null);
        setErroGlobal('');
        onHide();
        // Atualiza a listagem sempre que houve resultado (validação ou importação)
        if (hadResult && onJobConcluido) onJobConcluido();
    };

    const carregarProgramas = async (inputValue) => {
        try {
            const res = await api.get(`/programas/?nome=${inputValue}&limit=20`);
            return res.data.results.map(p => ({ value: p.id, label: p.nome }));
        } catch {
            return [];
        }
    };

    const handleDownload = async (tipo) => {
        if (!areaSelecionada) return;
        const setter = tipo === 'template' ? setBaixandoTemplate : setBaixandoDoc;
        setter(true);
        try {
            const res = tipo === 'template'
                ? await importacaoArquivosService.downloadTemplate(areaSelecionada.value)
                : await importacaoArquivosService.downloadDocumentacao(areaSelecionada.value);

            const ext = tipo === 'template' ? 'csv' : 'md';
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `importacao_${areaSelecionada.value}_${tipo}.${ext}`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch {
            setErroGlobal('Erro ao baixar o arquivo. Tente novamente.');
        } finally {
            setter(false);
        }
    };

    const handleDownloadCsv = async () => {
        if (!jobResult?.arquivo_download_url) return;
        setBaixandoCsv(true);
        try {
            // URL pré-assinada do S3 — abre diretamente para download
            const a = document.createElement('a');
            a.href = jobResult.arquivo_download_url;
            a.download = jobResult.nome_arquivo_original || 'importacao.csv';
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch {
            setErroGlobal('Erro ao baixar o arquivo CSV.');
        } finally {
            setBaixandoCsv(false);
        }
    };

    const handleValidar = async () => {
        if (!programaSelecionado || !areaSelecionada || !arquivo) return;

        setLoading(true);
        setErroGlobal('');
        setJobResult(null);

        const formData = new FormData();
        formData.append('arquivo_csv', arquivo);
        formData.append('programa_id', programaSelecionado.value);
        formData.append('area', areaSelecionada.value);

        try {
            const res = await importacaoArquivosService.validar(formData);
            setJobResult(res.data);
            setStep(3);
        } catch (err) {
            const data = err.response?.data;
            if (data && data.status === 'validado_com_erro') {
                setJobResult(data);
                setStep(3);
            } else {
                setErroGlobal(
                    (data && typeof data === 'object')
                        ? JSON.stringify(data)
                        : 'Erro ao validar o arquivo. Verifique e tente novamente.'
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmar = async () => {
        if (!jobResult?.id) return;
        setLoading(true);
        setErroGlobal('');
        try {
            const res = await importacaoArquivosService.confirmar({ job_id: jobResult.id });
            setJobResult(prev => ({ ...prev, ...res.data }));
            setStep(4);
            if (onJobConcluido) onJobConcluido();
        } catch (err) {
            setErroGlobal(err.response?.data?.detail || 'Erro ao confirmar a importação.');
        } finally {
            setLoading(false);
        }
    };

    const podeAvancar1 = programaSelecionado && areaSelecionada;
    const podeValidar = arquivo && !loading;
    const podeConfirmar = jobResult?.status === 'validado_ok' && !loading;

    const totalErrosJob = (jobResult?.erros ?? 0) + (jobResult?.warnings ?? 0);

    return (
        <Modal size="lg" show={show} onHide={handleClose} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Nova Importação de Arquivo</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Steps indicator */}
                {(() => {
                    const steps = ['Configuração', 'Upload & Validação', 'Resultado', 'Concluído'];
                    return (
                        <div className="d-flex align-items-flex-start mb-4" style={{ position: 'relative' }}>
                            {steps.map((label, i) => {
                                const done = step > i + 1;
                                const active = step === i + 1;
                                const isLast = i === steps.length - 1;
                                return (
                                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                                        {/* Linha conectora */}
                                        {!isLast && (
                                            <div style={{
                                                position: 'absolute',
                                                top: 16,
                                                left: '50%',
                                                width: '100%',
                                                height: 3,
                                                background: done ? '#28a745' : '#dee2e6',
                                                transition: 'background 0.3s ease',
                                                zIndex: 0,
                                            }} />
                                        )}
                                        {/* Círculo */}
                                        <div style={{
                                            position: 'relative',
                                            zIndex: 1,
                                            width: 32, height: 32,
                                            borderRadius: '50%',
                                            background: done ? '#28a745' : active ? '#28a745' : '#dee2e6',
                                            color: (done || active) ? '#fff' : '#9ca3af',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontWeight: 'bold', fontSize: 13,
                                            boxShadow: active ? '0 0 0 4px rgba(40,167,69,0.2)' : 'none',
                                            transition: 'all 0.3s ease',
                                        }}>
                                            {done ? '✓' : i + 1}
                                        </div>
                                        {/* Label */}
                                        <div style={{
                                            marginTop: 6, fontSize: 11,
                                            fontWeight: active ? 600 : 400,
                                            color: active ? '#28a745' : done ? '#28a745' : '#9ca3af',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {label}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })()}

                {erroGlobal && (
                    <Alert variant="danger" onClose={() => setErroGlobal('')} dismissible>
                        {erroGlobal}
                    </Alert>
                )}

                {/* STEP 1 */}
                {step === 1 && (
                    <div>
                        <Form.Group className="mb-3">
                            <Form.Label>Programa <span className="text-danger">*</span></Form.Label>
                            <AppAsyncSelect
                                loadOptions={carregarProgramas}
                                placeholder="Buscar programa..."
                                value={programaSelecionado}
                                onChange={setProgramaSelecionado}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Área de importação <span className="text-danger">*</span></Form.Label>
                            <AppSelect
                                options={AREAS}
                                placeholder="Selecione a área..."
                                value={areaSelecionada}
                                onChange={setAreaSelecionada}
                            />
                        </Form.Group>

                        {areaSelecionada?.value === 'prestadores' && (
                            <Alert variant="info" className="mt-3 mb-0">
                                <strong>Importante:</strong> O CSV de Prestadores deve conter a coluna obrigatória <code>categoria</code> com um dos valores abaixo por linha:
                                <div className="mt-2 d-flex flex-wrap gap-1">
                                    {CATEGORIAS_PRESTADORES.map(c => (
                                        <Badge key={c} bg="secondary" style={{ fontFamily: 'monospace', fontWeight: 'normal' }}>{c}</Badge>
                                    ))}
                                </div>
                            </Alert>
                        )}

                        {areaSelecionada && (
                            <div className="d-flex gap-2 mt-3">
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    disabled={baixandoTemplate}
                                    onClick={() => handleDownload('template')}
                                >
                                    {baixandoTemplate ? <Spinner size="sm" /> : <FaDownload className="me-1" />}
                                    Baixar Template CSV
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={baixandoDoc}
                                    onClick={() => handleDownload('documentacao')}
                                >
                                    {baixandoDoc ? <Spinner size="sm" /> : <FaDownload className="me-1" />}
                                    Baixar Documentação
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {/* STEP 2 */}
                {step === 2 && (
                    <div>
                        <p className="text-muted mb-3">
                            Envie o arquivo <strong>CSV</strong> seguindo o template baixado.
                            O arquivo será validado antes da importação real.
                        </p>
                        <Form.Group className="mb-3">
                            <Form.Label>Arquivo CSV <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="file"
                                accept=".csv"
                                onChange={e => setArquivo(e.target.files[0] || null)}
                            />
                            <Form.Text className="text-muted">
                                Formato: .csv · Encoding: UTF-8 · Delimitador: ponto-e-vírgula (;)
                            </Form.Text>
                        </Form.Group>
                        {arquivo && (
                            <Alert variant="info">
                                Arquivo selecionado: <strong>{arquivo.name}</strong>
                            </Alert>
                        )}
                    </div>
                )}

                {/* STEP 3 — Resultado da validação */}
                {step === 3 && jobResult && (
                    <div>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2">
                                {jobResult.status === 'validado_ok'
                                    ? <FaCheckCircle className="text-success" size={24} />
                                    : <FaTimesCircle className="text-danger" size={24} />
                                }
                                <div>
                                    <strong>Status:</strong>{' '}
                                    <Badge bg={STATUS_BADGE[jobResult.status] || 'secondary'}>
                                        {STATUS_LABEL[jobResult.status] || jobResult.status}
                                    </Badge>
                                </div>
                            </div>
                            {/* Botão de download do CSV enviado */}
                            {jobResult.arquivo_download_url && (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={baixandoCsv}
                                    onClick={handleDownloadCsv}
                                    title="Baixar o arquivo CSV enviado"
                                >
                                    {baixandoCsv ? <Spinner size="sm" /> : <FaDownload className="me-1" />}
                                    Baixar CSV
                                </Button>
                            )}
                        </div>

                        <Row className="mb-3">
                            {[
                                ['Total de linhas', jobResult.total_linhas],
                                ['Erros', jobResult.erros],
                                ['Warnings', jobResult.warnings],
                            ].map(([label, val]) => (
                                <Col xs={4} key={label} className="text-center">
                                    <div style={{ fontSize: 22, fontWeight: 'bold' }}>{val}</div>
                                    <div className="text-muted" style={{ fontSize: 12 }}>{label}</div>
                                </Col>
                            ))}
                        </Row>

                        {jobResult.mensagem_resumo && (
                            <Alert variant={jobResult.status === 'validado_ok' ? 'success' : 'warning'}>
                                {jobResult.mensagem_resumo}
                            </Alert>
                        )}

                        {totalErrosJob > 0 && (
                            <TabelaErros
                                jobId={jobResult.id}
                                errosIniciais={jobResult.erros_detalhes || []}
                                totalErros={totalErrosJob}
                            />
                        )}
                    </div>
                )}

                {/* STEP 4 — Concluído */}
                {step === 4 && jobResult && (
                    <div className="text-center py-3">
                        <FaCheckCircle className="text-success mb-3" size={48} />
                        <h5>Importação concluída!</h5>
                        {jobResult.arquivo_download_url && (
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                className="mb-3"
                                disabled={baixandoCsv}
                                onClick={handleDownloadCsv}
                            >
                                {baixandoCsv ? <Spinner size="sm" /> : <FaDownload className="me-1" />}
                                Baixar CSV da importação
                            </Button>
                        )}
                        <Row className="mt-3">
                            {[
                                ['Importadas', jobResult.importadas, 'success'],
                                ['Atualizadas', jobResult.atualizadas, 'info'],
                                ['Ignoradas', jobResult.ignoradas, 'secondary'],
                                ['Erros', jobResult.erros, 'danger'],
                            ].map(([label, val, color]) => (
                                <Col xs={3} key={label} className="text-center">
                                    <div style={{ fontSize: 24, fontWeight: 'bold', color: `var(--bs-${color})` }}>{val}</div>
                                    <div className="text-muted" style={{ fontSize: 12 }}>{label}</div>
                                </Col>
                            ))}
                        </Row>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-danger" onClick={handleClose}>
                    {step === 4 ? 'Fechar' : 'Cancelar'}
                </Button>
                {step === 1 && (
                    <Button variant="primary" disabled={!podeAvancar1} onClick={() => setStep(2)}>
                        Próximo
                    </Button>
                )}
                {step === 2 && (
                    <>
                        <Button variant="light" onClick={() => setStep(1)}>Voltar</Button>
                        <Button variant="primary" disabled={!podeValidar} onClick={handleValidar}>
                            {loading ? <Spinner size="sm" className="me-1" /> : <FaUpload className="me-1" />}
                            Validar arquivo
                        </Button>
                    </>
                )}
                {step === 3 && jobResult?.status !== 'validado_ok' && (
                    <Button variant="light" onClick={() => setStep(2)}>Tentar novamente</Button>
                )}
                {step === 3 && jobResult?.status === 'validado_ok' && (
                    <Button variant="success" disabled={!podeConfirmar} onClick={handleConfirmar}>
                        {loading ? <Spinner size="sm" className="me-1" /> : <FaCheckCircle className="me-1" />}
                        Confirmar Importação
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}
